import { TERRAIN } from '../core/SimConfig.js';

/**
 * Drivable road graph, baked from a hand-drawn SVG by tools/build-roads.mjs.
 *
 * Nodes are junctions; edges are polylines between them. A vehicle traverses an
 * edge in one direction, and on reaching a node picks a new (edge, direction)
 * pair to continue on — which is what lets a column turn at an intersection
 * rather than being confined to one stroke. Dead ends (degree-1 nodes) are the
 * only place a U-turn is issued.
 *
 * The bake stores normalised [0,1] grid UV, so the terrain dimensions live in
 * exactly one place (TERRAIN) and a grid resize needs no rebuild. Everything
 * this class exposes is world metres: UV is converted once here, and arc
 * lengths are accumulated at load so `s` is a real distance the AI can compare
 * against speeds and lookaheads. Y is never stored — vehicles get it from the
 * terrain collider like any other ground contact.
 *
 * UV convention matches VegetationSplatMap: u→+worldX (east), v→+worldZ (south).
 */
export default class RoadNetwork {
  /** @param {object | null} data — parsed roads.json, or null if unavailable */
  constructor(data) {
    /** @type {{pts: Float32Array, cum: Float32Array, length: number, from: number, to: number}[]} */
    this.edges = [];
    /** @type {{x: number, z: number, exits: {edge: number, dir: number}[]}[]} */
    this.nodes = [];
    this.totalLength = 0;

    if (!data?.edges?.length || !data?.nodes?.length) return;

    const { cols, rows, tileSize, origin } = TERRAIN;
    const [ox, oz] = origin;
    const minX = ox - (cols * tileSize) / 2;
    const minZ = oz - (rows * tileSize) / 2;
    const spanX = cols * tileSize;
    const spanZ = rows * tileSize;

    this.nodes = data.nodes.map(([u, v]) => ({
      x: minX + u * spanX,
      z: minZ + v * spanZ,
      exits: [],
    }));

    for (const raw of data.edges) {
      const n = raw.pts.length / 2;
      if (n < 2) continue;

      const pts = new Float32Array(n * 2);
      for (let i = 0; i < n; i++) {
        pts[i * 2]     = minX + raw.pts[i * 2] * spanX;
        pts[i * 2 + 1] = minZ + raw.pts[i * 2 + 1] * spanZ;
      }

      const cum = new Float32Array(n);
      for (let i = 1; i < n; i++) {
        cum[i] = cum[i - 1] + Math.hypot(pts[i * 2] - pts[i * 2 - 2], pts[i * 2 + 1] - pts[i * 2 - 1]);
      }

      const length = cum[n - 1];
      if (!(length > 0)) continue;

      const index = this.edges.length;
      this.edges.push({ pts, cum, length, from: raw.from, to: raw.to });
      this.totalLength += length;

      // An edge can be left from its `from` node heading forward, or from its
      // `to` node heading backward. A loop edge (from === to) offers both at
      // the same node, which is correct — a roundabout is drivable either way.
      this.nodes[raw.from]?.exits.push({ edge: index, dir: 1 });
      this.nodes[raw.to]?.exits.push({ edge: index, dir: -1 });
    }
  }

  /** True once at least one drivable edge exists. */
  get ready() { return this.edges.length > 0; }

  /**
   * Position and unit tangent at arc length `s` along an edge, measured from
   * its `from` end. `s` is clamped, so callers can sample past either end
   * without special-casing — the route queue handles crossing junctions.
   *
   * @param {object} out — mutated with { x, z, tx, tz }; tangent points from→to
   */
  sample(edgeIndex, s, out) {
    const e = this.edges[edgeIndex];
    const { pts, cum, length } = e;
    const n = cum.length;
    const d = s <= 0 ? 0 : (s >= length ? length : s);

    // Binary search for the segment containing d.
    let lo = 0, hi = n - 1;
    while (lo < hi - 1) {
      const mid = (lo + hi) >> 1;
      if (cum[mid] <= d) lo = mid; else hi = mid;
    }

    const ax = pts[lo * 2],     az = pts[lo * 2 + 1];
    const bx = pts[lo * 2 + 2], bz = pts[lo * 2 + 3];
    const segLen = cum[lo + 1] - cum[lo];
    const t = segLen > 1e-6 ? (d - cum[lo]) / segLen : 0;

    out.x = ax + (bx - ax) * t;
    out.z = az + (bz - az) * t;
    const inv = segLen > 1e-6 ? 1 / segLen : 0;
    out.tx = (bx - ax) * inv;
    out.tz = (bz - az) * inv;
    return out;
  }

  /** The node a traversal of `edgeIndex` in `dir` arrives at. */
  endNode(edgeIndex, dir) {
    const e = this.edges[edgeIndex];
    return dir > 0 ? e.to : e.from;
  }

  /**
   * Choose the next (edge, dir) to take on arriving at a node.
   *
   * Doubling back is excluded so a vehicle drives *through* a junction instead
   * of bouncing, and is only allowed when it's the sole option — i.e. a dead
   * end, where turning around is the intended behaviour. Note the exclusion is
   * keyed on the traversal (edge *and* direction), not the edge alone, so a
   * loop edge can still be re-entered the other way round.
   *
   * @returns {{edge: number, dir: number} | null}
   */
  pickExit(nodeIndex, fromEdge = -1, fromDir = 0) {
    const node = this.nodes[nodeIndex];
    if (!node || !node.exits.length) return null;

    const options = node.exits.filter((x) => !(x.edge === fromEdge && x.dir === -fromDir));
    const pool = options.length ? options : node.exits;
    return pool[(Math.random() * pool.length) | 0];
  }

  /**
   * Commit a chain of traversals covering at least `distance` metres from
   * (edge, dir, s), following random exits at each node.
   *
   * Used for one-shot walks — laying a spawning column out backwards down the
   * road it is arriving on, which has to be able to run back through a junction
   * rather than stopping at the end of one edge. Route *following* extends its
   * queue incrementally instead, so it can re-roll junctions as it drives.
   *
   * @returns {{edge: number, dir: number}[]}
   */
  walkFrom(edge, dir, s, distance) {
    const entries = [{ edge, dir }];
    if (!this.ready) return entries;

    let available = dir > 0 ? this.edges[edge].length - s : s;
    while (available < distance && entries.length < 16) {
      const last = entries[entries.length - 1];
      const exit = this.pickExit(this.endNode(last.edge, last.dir), last.edge, last.dir);
      if (!exit) break;
      entries.push(exit);
      available += this.edges[exit.edge].length;
    }
    return entries;
  }

  /**
   * Point `distAhead` metres along a traversal chain that begins at `startS`
   * on its first entry, walking across the entries as needed. The tangent is
   * in the direction of travel, so a chain built backwards reports a backwards
   * tangent — negate it for a vehicle's facing.
   *
   * @param {object} out — mutated with { x, z, tx, tz }
   */
  sampleRoute(entries, startS, distAhead, out) {
    let remaining = distAhead;

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const edge = this.edges[entry.edge];
      const s = i === 0 ? startS : (entry.dir > 0 ? 0 : edge.length);
      const available = entry.dir > 0 ? edge.length - s : s;

      if (remaining <= available || i === entries.length - 1) {
        this.sample(entry.edge, s + entry.dir * remaining, out);
        out.tx *= entry.dir;
        out.tz *= entry.dir;
        return out;
      }
      remaining -= available;
    }

    return out;
  }

  /**
   * Nearest point on the whole network to a world XZ, as {edge, s, dist}.
   *
   * A full scan — every segment of every edge — so this is only for one-off
   * events, not per-frame use. It exists for the case where a follower loses
   * its leader mid-route and has to rejoin the road on its own; route
   * following itself re-projects within a local window instead, precisely so
   * it can't snap across the map where a road passes near itself.
   *
   * @returns {{edge: number, s: number, dist: number} | null}
   */
  nearest(x, z) {
    if (!this.ready) return null;

    let best = null;
    let bestD2 = Infinity;

    for (let ei = 0; ei < this.edges.length; ei++) {
      const { pts, cum } = this.edges[ei];
      const n = cum.length;
      for (let i = 0; i < n - 1; i++) {
        const ax = pts[i * 2],     az = pts[i * 2 + 1];
        const bx = pts[i * 2 + 2], bz = pts[i * 2 + 3];
        const dx = bx - ax, dz = bz - az;
        const segLen2 = dx * dx + dz * dz;
        if (segLen2 < 1e-9) continue;

        const t = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / segLen2));
        const px = ax + dx * t, pz = az + dz * t;
        const d2 = (x - px) * (x - px) + (z - pz) * (z - pz);
        if (d2 < bestD2) {
          bestD2 = d2;
          best = { edge: ei, s: cum[i] + t * Math.sqrt(segLen2), dist: 0 };
        }
      }
    }

    if (best) best.dist = Math.sqrt(bestD2);
    return best;
  }

  /**
   * Arc length of the point on `edgeIndex` closest to a world XZ, searched only
   * within ±`window` metres of `nearS`. This is the per-frame drift correction
   * for route following: bounding the search is what keeps a vehicle from
   * jumping to a distant part of the same edge where the road doubles back.
   */
  projectLocal(edgeIndex, nearS, x, z, window) {
    const { pts, cum, length } = this.edges[edgeIndex];
    const n = cum.length;
    const lo = Math.max(0, nearS - window);
    const hi = Math.min(length, nearS + window);

    let bestS = nearS;
    let bestD2 = Infinity;

    for (let i = 0; i < n - 1; i++) {
      // Skip segments entirely outside the window.
      if (cum[i + 1] < lo) continue;
      if (cum[i] > hi) break;

      const ax = pts[i * 2],     az = pts[i * 2 + 1];
      const bx = pts[i * 2 + 2], bz = pts[i * 2 + 3];
      const dx = bx - ax, dz = bz - az;
      const segLen2 = dx * dx + dz * dz;
      if (segLen2 < 1e-9) continue;

      const t = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / segLen2));
      const px = ax + dx * t, pz = az + dz * t;
      const d2 = (x - px) * (x - px) + (z - pz) * (z - pz);
      if (d2 < bestD2) {
        bestD2 = d2;
        bestS = cum[i] + t * Math.sqrt(segLen2);
      }
    }

    return Math.max(lo, Math.min(hi, bestS));
  }
}
